/**
 * Bundle widget — "Complete your bundle"
 *
 * No fetch('/cart/add.js') happens in this file for real adds. All cart-add
 * logic (analytics, cart attribution, on:cart:add / on:line-item:change
 * events, drawer rendering, cart icon update) is delegated to
 * ProductForm.submitItems(), the same static method the theme's native
 * Add to Cart form uses (see product-form.js). This guarantees bundle
 * purchases are indistinguishable from normal purchases to every other
 * script on the site.
 *
 * DISCOUNT PERCENT: comes entirely from data-discount-percent, rendered
 * server-side by bundle-widget.liquid from block.settings.bundle_discount_percent
 * on the "Bundle accessories" block (Theme Customizer). Update the discount
 * by editing that setting, matching whatever Admin → Discounts says, no
 * code change needed.
 *
 * SUMMARY VISIBILITY: the summary row (data-bundle-summary) is rendered
 * `hidden` by bundle-widget.liquid. recalculate() below removes/re-adds
 * that attribute based on whether at least one accessory is checked, so
 * shoppers never see a "$0.00 / + you save $0.00" line before selecting
 * anything.
 *
 * TEXT / TRANSLATION: this file contains NO hardcoded shopper-facing
 * strings. Every piece of text (summary label, "you save" text, and the
 * two error messages used in addBundle()) is passed in from
 * bundle-widget.liquid via data-summary-label / data-save-text /
 * data-variant-error-text / data-generic-error-text, which themselves come
 * from block.settings (Theme Customizer). This lets the Translate & Adapt
 * app detect and translate every string with no locale JSON files and no
 * code edits required to add a language.
 *
 * PLACEHOLDER SYNTAX: templates use square-bracket placeholders —
 * [count] and [amount] — NOT {{ count }} / {{ amount }}. Curly braces are
 * reserved by the Shopify Theme Editor for "Connect dynamic source" on
 * text/richtext settings; using {{ }} there causes a
 * "Change or remove the dynamic source(s) with errors" error in the
 * customizer. Square brackets are swapped in below via simple
 * String.replace() calls, same as the Liquid side does for [discount] and
 * [product].
 *
 * This widget intercepts clicks on three existing buttons, only when at
 * least one accessory is checked — otherwise every one of them behaves
 * exactly as before, untouched:
 *   1. The main product form's Add to cart button (found inside data-main-form-id)
 *   2. The sticky bottom bar's Add to cart button   (found inside data-sticky-form-id)
 *   3. The #buy-now button
 * Interception happens on 'click' (capture phase), not 'submit' — some of
 * these buttons have their own click handler elsewhere in the theme that
 * calls preventDefault() before a submit event would ever fire, so 'click'
 * is the only point guaranteed to run first for all three.
 */
(function () {
  class BundleWidget extends HTMLElement {
    constructor() {
      super();
      // Rendered server-side by bundle-widget.liquid — see file header.
      this.discountPercent = parseFloat(this.dataset.discountPercent || '20');
      this.accessories = {};
      this._onActionClick = this.handleActionClick.bind(this);
      // Guards against a second button (or a second click on the same
      // button) firing another /cart/add.js request while one is already
      // in flight — mirrors the aria-disabled check ProductForm.handleSubmit
      // does for the native single-item form.
      this._submitting = false;
    }

    connectedCallback() {
      // Text templates — all sourced from block settings via data-*
      // attributes rendered by bundle-widget.liquid. No hardcoded English
      // strings live in this file; see file header. Templates use
      // [count] / [amount] placeholders, NOT {{ }}.
      this.summaryLabelTemplate = this.dataset.summaryLabel || '';
      this.saveTemplate = this.dataset.saveText || '';
      this.variantErrorText = this.dataset.variantErrorText || '';
      this.genericErrorText = this.dataset.genericErrorText || '';

      this.parseAccessoryData();
      this.bindEvents();
      this.cacheMainButtonRefs();

      // Capture phase, on the click itself rather than the form's submit
      // event. Some buttons (e.g. the sticky Add to cart bar) have their
      // own click handler that calls preventDefault() on click — which
      // means the browser's native form submission, and therefore any
      // 'submit' event, never fires at all for that button. Intercepting
      // 'click' in the capture phase runs before any of that, so it's the
      // only interception point that reliably covers every button.
      document.addEventListener('click', this._onActionClick, true);

      this.recalculate();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onActionClick, true);
    }

    cacheMainButtonRefs() {
      this.mainFormId = this.dataset.mainFormId || null;
      this.stickyFormId = this.dataset.stickyFormId || null;

      const mainForm = this.mainFormId ? document.getElementById(this.mainFormId) : null;
      const stickyForm = this.stickyFormId ? document.getElementById(this.stickyFormId) : null;

      this.mainForm = mainForm;
      this.stickyForm = stickyForm;
      this.mainAddBtn = mainForm ? mainForm.querySelector('[name="add"]') : null;
      this.stickyAddBtn = stickyForm ? stickyForm.querySelector('[name="add"]') : null;
      this.buyNowBtn = document.getElementById('buy-now');

      // Cache each button's already-localized label (data-add-to-cart-text
      // is already rendered via {{ 'products.product.add_to_cart' | t }} in
      // the theme's Liquid — reuse it instead of hardcoding new English).
      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (btn && !btn.dataset.addToCartText) {
          btn.dataset.addToCartText = btn.textContent.trim();
        }
      });
      if (this.buyNowBtn) {
        this.buyNowBaseText = this.buyNowBtn.textContent.trim();
      }
    }

    parseAccessoryData() {
      const cards = this.querySelectorAll('[data-accessory-card]');
      cards.forEach((card) => {
        const id = card.dataset.accessoryId;
        const dataScript = this.querySelector(`script[data-accessory-variants="${id}"]`);
        if (!dataScript) return;

        let data;
        try {
          data = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Bundle widget: could not parse variant data for', id, e);
          return;
        }

        let firstAvailable = null;
        const orderedValues = data.firstOptionValues || [];
        for (const val of orderedValues) {
          const match = data.variants.find((v) => v.available && v.options[0] === val);
          if (match) {
            firstAvailable = match;
            break;
          }
        }
        if (!firstAvailable) {
          firstAvailable = data.variants.find((v) => v.available) || data.variants[0];
        }

        this.accessories[id] = {
          available: data.available,
          variants: data.variants,
          selectedOptions: firstAvailable ? firstAvailable.options.slice() : [],
          checked: false,
          currentVariant: firstAvailable || null,
        };

        this.updatePriceDisplay(id);
      });
    }

    bindEvents() {
      this.querySelectorAll('[data-accessory-checkbox]').forEach((cb) => {
        cb.addEventListener('change', (e) => {
          const id = e.target.dataset.accessoryId;
          if (this.accessories[id]) {
            this.accessories[id].checked = e.target.checked;
            this.recalculate();
          }
        });
      });

      this.querySelectorAll('[data-option-value]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const id = e.target.dataset.accessoryId;
          const optionIndex = parseInt(e.target.dataset.optionIndex, 10);
          const value = e.target.dataset.optionValue;
          this.selectOption(id, optionIndex, value);
        });
      });
    }

    selectOption(id, optionIndex, value) {
      const accessory = this.accessories[id];
      if (!accessory) return;

      accessory.selectedOptions[optionIndex] = value;

      const match = accessory.variants.find((v) =>
        v.options.every(
          (optVal, i) => accessory.selectedOptions[i] == null || optVal === accessory.selectedOptions[i]
        )
      );
      if (match) accessory.currentVariant = match;

      const optionsWrap = this.querySelector(`[data-accessory-options][data-accessory-id="${id}"]`);
      if (optionsWrap) {
        const row = optionsWrap.querySelector(`[data-option-index="${optionIndex}"]`);
        if (row) {
          row.querySelectorAll('[data-option-value]').forEach((b) => {
            b.classList.toggle('is-selected', b.dataset.optionValue === value);
          });
        }
      }

      this.updatePriceDisplay(id);
      this.recalculate();
    }

    updatePriceDisplay(id) {
      const accessory = this.accessories[id];
      if (!accessory || !accessory.currentVariant) return;

      const priceWrap = this.querySelector(`[data-accessory-price][data-accessory-id="${id}"]`);
      if (!priceWrap) return;

      const valueEl = priceWrap.querySelector('[data-price-value]');
      if (valueEl) valueEl.textContent = this.formatMoney(accessory.currentVariant.price);
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        try {
          return Shopify.formatMoney(cents, window.theme_money_format || '${{amount}}');
        } catch (e) {
          /* fall through */
        }
      }
      return '$' + (cents / 100).toFixed(2);
    }

    getCheckedAccessoryCount() {
      return Object.values(this.accessories).filter((a) => a.checked && a.currentVariant).length;
    }

    recalculate() {
      let totalCents = 0;
      let saveCents = 0;
      let count = 0;

      Object.values(this.accessories).forEach((accessory) => {
        if (accessory.checked && accessory.currentVariant) {
          const original = accessory.currentVariant.price;
          const discounted = Math.round(original * (1 - this.discountPercent / 100));
          totalCents += discounted;
          saveCents += original - discounted;
          count += 1;
        }
      });

      const summaryEl = this.querySelector('[data-bundle-summary]');
      const countLabel = this.querySelector('[data-bundle-count-label]');
      const totalEl = this.querySelector('[data-bundle-total]');
      const saveEl = this.querySelector('[data-bundle-save]');

      // Summary stays out of view until something is actually selected —
      // avoids showing a confusing "$0.00 / + you save $0.00" line.
      if (summaryEl) {
        summaryEl.hidden = count === 0;
      }

      // Label text comes from the merchant-editable template
      // (block.settings.summary_label_text), passed down as
      // data-summary-label. [count] is swapped for the live count here —
      // no hardcoded English pluralization logic in this file.
      if (countLabel && this.summaryLabelTemplate) {
        countLabel.textContent = this.summaryLabelTemplate.replace('[count]', count);
      }
      if (totalEl) totalEl.textContent = this.formatMoney(totalCents);
      if (saveEl && this.saveTemplate) {
        saveEl.textContent = this.saveTemplate.replace('[amount]', this.formatMoney(saveCents));
      }

      this.updateButtonLabels(count);
    }

    /**
     * Appends the checked-item count to each button's own already-translated
     * label instead of introducing new hardcoded English text.
     */
    updateButtonLabels(count) {
      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        const base = btn.dataset.addToCartText;
        if (!base) return;
        btn.textContent = base;
      });

      if (
        this.buyNowBtn &&
        !this.buyNowBtn.disabled &&
        this.buyNowBtn.getAttribute('aria-disabled') !== 'true' &&
        this.buyNowBaseText
      ) {
        this.buyNowBtn.textContent = this.buyNowBaseText;
      }
    }

    getMainVariantId() {
      const input = this.mainForm ? this.mainForm.querySelector('[name="id"]') : null;
      return input ? input.value : null;
    }

    getMainQuantity() {
      const qtyInput = this.mainForm ? this.mainForm.querySelector('[name="quantity"]') : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return qty > 0 ? qty : 1;
    }

    buildBundleItems() {
      const mainVariantId = this.getMainVariantId();
      if (!mainVariantId) return null;

      const items = [{ id: mainVariantId, quantity: this.getMainQuantity() }];
      Object.values(this.accessories).forEach((accessory) => {
        if (accessory.checked && accessory.currentVariant) {
          items.push({ id: accessory.currentVariant.id, quantity: 1 });
        }
      });
      return items;
    }

    handleActionClick(event) {
      const clickedBtn = event.target.closest('button');
      if (!clickedBtn) return;
      if (clickedBtn !== this.mainAddBtn && clickedBtn !== this.stickyAddBtn && clickedBtn !== this.buyNowBtn) return;
      if (this.getCheckedAccessoryCount() === 0) return; // untouched native flow

      // A bundle add is already in flight (from this button or one of the
      // other two) — ignore this click instead of firing a second
      // /cart/add.js request.
      if (this._submitting) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addBundle({ redirectToCheckout: clickedBtn === this.buyNowBtn, triggerBtn: clickedBtn });
    }

    async addBundle({ redirectToCheckout, triggerBtn }) {
      const errorEl = this.querySelector('[data-bundle-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const items = this.buildBundleItems();
      if (!items) {
        // Text sourced from block.settings.variant_error_text via
        // data-variant-error-text — no hardcoded string here.
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = this.variantErrorText;
        }
        return;
      }

      this._submitting = true;

      // All three buttons get aria-disabled so a click on a *different*
      // button can't fire a second request while this one is in flight —
      // but only the button the shopper actually clicked gets the visual
      // loading spinner.
      const allButtons = [this.mainAddBtn, this.stickyAddBtn, this.buyNowBtn].filter(Boolean);
      allButtons.forEach((b) => {
        b.setAttribute('aria-disabled', 'true');
        if (b === triggerBtn) b.classList.add('is-loading');
      });

      const ProductFormClass = customElements.get('product-form');
      let succeeded = false;

      try {
        const data = await ProductFormClass.submitItems(items);
        succeeded = true;

        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }

        ProductFormClass.updateCartIcon(data);

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        }
      } catch (err) {
        // err.cartError / err.message come from Shopify's own /cart/add.js
        // response and are already localized by Shopify for the storefront's
        // active language — left as-is. Only the fallback (no error message
        // returned) uses our merchant-editable, translatable string.
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = (err && (err.cartError || err.message)) || this.genericErrorText;
        }
        document.dispatchEvent(
          new CustomEvent('on:cart:error', { bubbles: true, detail: { error: errorEl ? errorEl.textContent : null } })
        );
      } finally {
        this._submitting = false;
        allButtons.forEach((b) => {
          if (b === triggerBtn && succeeded) b.classList.add('is-success');
          b.removeAttribute('aria-disabled');
          setTimeout(() => {
            b.classList.remove('is-loading');
            b.classList.remove('is-success');
          }, 1400);
        });
        this.updateButtonLabels(this.getCheckedAccessoryCount());
      }
    }
  }

  if (!customElements.get('bundle-widget')) {
    customElements.define('bundle-widget', BundleWidget);
  }
})();